import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import LoadingState from "../components/LoadingState";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from "../components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { UserRound, Plus, Loader2, Pencil } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

const ROLE_LABELS = {
    amministratore: "Amministratore aziendale",
    analista: "Analista",
    consultazione: "Consultazione",
};

function emptyForm() {
    return {
        email: "",
        full_name: "",
        password: "",
        company_role: "consultazione",
        company_id: "",
        role_global: null,
    };
}

export default function AdminUsersPage() {
    const { isSuperAdmin, companyId, companies } = useAuth();
    const [users, setUsers] = useState([]);
    const [companiesList, setCompaniesList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState(emptyForm());
    const [saving, setSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);

    const isGlobal = isSuperAdmin() && companyId === "__all__";

    const load = async () => {
        setLoading(true);
        try {
            if (isGlobal) {
                const [u, c] = await Promise.all([
                    api.get("/admin/users"),
                    api.get("/admin/companies"),
                ]);
                setUsers(u.data || []);
                setCompaniesList(c.data || []);
            } else if (isSuperAdmin()) {
                const [u, c] = await Promise.all([
                    api.get(`/admin/users?company_id=${companyId}`),
                    api.get("/admin/companies"),
                ]);
                setUsers(u.data || []);
                setCompaniesList(c.data || []);
            } else {
                const { data } = await api.get("/company/users");
                setUsers(data || []);
                setCompaniesList([]);
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, isGlobal]);

    const openCreate = () => {
        setEditingId(null);
        setForm({
            ...emptyForm(),
            company_id: !isGlobal && companyId && companyId !== "__all__" ? companyId : "",
        });
        setDialogOpen(true);
    };

    const save = async (ev) => {
        ev.preventDefault();
        if (!form.email || !form.full_name || !form.password) return toast.error("Compila tutti i campi obbligatori");
        if (form.password.length < 8) return toast.error("Password minima 8 caratteri");
        setSaving(true);
        try {
            if (isSuperAdmin()) {
                await api.post("/admin/users", {
                    email: form.email.toLowerCase(),
                    full_name: form.full_name,
                    password: form.password,
                    role_global: form.role_global || null,
                    is_active: true,
                    company_id: form.company_id || null,
                    company_role: form.company_id ? form.company_role : null,
                });
            } else {
                await api.post("/company/users", {
                    email: form.email.toLowerCase(),
                    full_name: form.full_name,
                    password: form.password,
                    company_role: form.company_role,
                    is_active: true,
                });
            }
            toast.success("Utente creato");
            setDialogOpen(false);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore");
        } finally {
            setSaving(false);
        }
    };

    const columns = [
        {
            key: "full_name", header: "Utente", sortable: true, render: (r) => (
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-[#2563EB]/10 flex items-center justify-center">
                        <UserRound className="w-4 h-4 text-[#2563EB]" />
                    </div>
                    <div>
                        <div className="text-sm font-medium text-[#172033]">{r.full_name}</div>
                        <div className="text-xs text-[#64748B]">{r.email}</div>
                    </div>
                </div>
            ),
        },
        {
            key: "role_global", header: "Ruolo globale", render: (r) => (
                r.role_global === "super_admin"
                    ? <span className="text-xs font-semibold text-[#2563EB]">Super Admin</span>
                    : <span className="text-xs text-[#64748B]">—</span>
            ),
        },
        {
            key: "company_role", header: "Ruolo aziendale", render: (r) => (
                r.company_role
                    ? <span className="text-xs font-medium text-[#172033]">{ROLE_LABELS[r.company_role] || r.company_role}</span>
                    : Array.isArray(r.companies) && r.companies.length > 0
                        ? <span className="text-xs text-[#64748B]">{r.companies.length} aziende</span>
                        : <span className="text-xs text-[#64748B]">—</span>
            ),
        },
        {
            key: "is_active", header: "Stato", render: (r) => (
                r.is_active
                    ? <span className="text-xs text-[#16A34A] font-semibold">Attivo</span>
                    : <span className="text-xs text-[#DC2626] font-semibold">Disattivato</span>
            ),
        },
        {
            key: "last_login_at", header: "Ultimo accesso", render: (r) => (
                r.last_login_at ? <span className="text-xs text-[#64748B]">{new Date(r.last_login_at).toLocaleString("it-IT")}</span> : <span className="text-xs text-[#94A3B8]">Mai</span>
            ),
        },
    ];

    return (
        <div className="space-y-6">
            <PageHeader
                title="Utenti"
                subtitle={isSuperAdmin() ? "Gestisci tutti gli utenti della piattaforma." : "Gestisci gli utenti della tua azienda."}
                breadcrumbs={[{ label: "Amministrazione" }, { label: "Utenti" }]}
                actions={
                    <Button
                        onClick={openCreate}
                        data-testid="new-user-btn"
                        className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white gap-2"
                    >
                        <Plus className="w-4 h-4" />
                        Nuovo utente
                    </Button>
                }
            />

            {loading ? (
                <LoadingState />
            ) : (
                <DataTable
                    testId="users-table"
                    columns={columns}
                    data={users}
                    pageSize={20}
                    emptyTitle="Nessun utente"
                    emptyDescription="Crea il primo utente."
                    getRowKey={(r) => r.id}
                />
            )}

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="bg-white sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Nuovo utente</DialogTitle>
                        <DialogDescription>Crea un nuovo account e assegna un ruolo.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={save} className="space-y-4" data-testid="user-form">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Nome completo</Label>
                                <Input data-testid="user-fullname-input" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="mt-1 h-10" />
                            </div>
                            <div>
                                <Label>Email</Label>
                                <Input type="email" data-testid="user-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 h-10" />
                            </div>
                        </div>
                        <div>
                            <Label>Password iniziale</Label>
                            <Input type="password" data-testid="user-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 h-10" placeholder="Almeno 8 caratteri" />
                        </div>

                        {isSuperAdmin() && (
                            <div>
                                <Label>Azienda</Label>
                                <Select value={form.company_id || "__none__"} onValueChange={(v) => setForm({ ...form, company_id: v === "__none__" ? "" : v })}>
                                    <SelectTrigger className="mt-1 h-10" data-testid="user-company-select"><SelectValue placeholder="Nessuna azienda" /></SelectTrigger>
                                    <SelectContent className="bg-white">
                                        <SelectItem value="__none__">Nessuna (solo Super Admin)</SelectItem>
                                        {companiesList.map((c) => (
                                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {(form.company_id || !isSuperAdmin()) && (
                            <div>
                                <Label>Ruolo aziendale</Label>
                                <Select value={form.company_role} onValueChange={(v) => setForm({ ...form, company_role: v })}>
                                    <SelectTrigger className="mt-1 h-10" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                                    <SelectContent className="bg-white">
                                        <SelectItem value="amministratore">Amministratore aziendale</SelectItem>
                                        <SelectItem value="analista">Analista</SelectItem>
                                        <SelectItem value="consultazione">Consultazione</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {isSuperAdmin() && (
                            <div className="flex items-center gap-2 pt-1">
                                <input
                                    id="rg"
                                    type="checkbox"
                                    checked={form.role_global === "super_admin"}
                                    onChange={(e) => setForm({ ...form, role_global: e.target.checked ? "super_admin" : null })}
                                    data-testid="user-super-admin-check"
                                />
                                <Label htmlFor="rg" className="cursor-pointer">
                                    Concedi ruolo Super Admin globale
                                </Label>
                            </div>
                        )}

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
                            <Button type="submit" disabled={saving} data-testid="user-save-btn" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white">
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crea utente"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
